import { Box, Button, Container, Stack, Typography } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FloatingActionButton, LinearBar } from 'shared/components';
import { useLocalStorage } from 'shared/hooks';
import { useUpdateCategory, useUploadParameters } from 'shared/mutations';
import { Category } from 'shared/types';
import { MarginTargetsSection } from './margin-targets-section';
import { PositionToCompetitorsSection } from './position-to-competitors-section';
import { TierCategorySection } from './tier-category-section';

interface CategoryPageProps {
    category: Category;
    isFetching: boolean;
}

interface FloatingActionButtonState {
    timeStamp: string;
}

export function CategoryPage({ category, isFetching }: CategoryPageProps) {
    const updateMutation = useUpdateCategory();
    const uploadMutation = useUploadParameters();

    const [state, setState] = useLocalStorage<FloatingActionButtonState>('floating-action-button', { timeStamp: '' });

    const methods = useForm({ defaultValues: category });

    const { handleSubmit, formState, reset } = methods;

    useEffect(() => {
        reset(category);
    }, [category, reset]);

    const resetForm = useCallback(() => {
        if (category) {
            reset(category);
        }
    }, [reset, category]);

    const onSubmit = async (category: Category) => {
        await updateMutation.mutateAsync(category);
    };

    const uploadParameters = async () => {
        const today = new Date();
        today.setTime(today.getTime() + 30 * 1000); // adding 30 seconds
        setState(prev => ({ ...prev, timeStamp: today.toISOString() }));

        await uploadMutation.mutateAsync();
    };

    useEffect(() => {
        let id: NodeJS.Timer;
        if (state.timeStamp) {
            id = setInterval(() => {
                const now = new Date();
                const timeStamp = new Date(state.timeStamp);
                if (now > timeStamp) {
                    setState({ timeStamp: '' });
                }
            }, 1000);
        }
        return () => {
            clearInterval(id);
        };
    }, [state.timeStamp, setState]);

    const disable = Boolean(state.timeStamp);

    return (
        <>
            <LinearBar loading={isFetching} />
            <Container sx={{ position: 'relative', pt: 2 }} maxWidth={false}>
                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Typography textAlign='center' variant='h4'>
                            Model Parameters for {category.name}
                        </Typography>
                        <Box
                            position='absolute'
                            top={16}
                            right={16}
                            sx={theme => ({ zIndex: theme.zIndex.appBar + 1 })}
                        >
                            {formState.isDirty ? (
                                <Stack spacing={2} direction='row'>
                                    <Button
                                        disabled={formState.isSubmitting}
                                        type='submit'
                                        color='secondary'
                                        variant='contained'
                                    >
                                        save
                                    </Button>
                                    <Button disabled={formState.isSubmitting} onClick={resetForm} variant='outlined'>
                                        reset
                                    </Button>
                                </Stack>
                            ) : null}
                        </Box>
                        <Stack spacing={2}>
                            <TierCategorySection categoryName={'Tier Categories'} data={category.tierCategories} />
                            <PositionToCompetitorsSection data={category.positionToCompetitors} />
                            <MarginTargetsSection data={category.marginTargets} />
                        </Stack>
                    </form>
                </FormProvider>
                <FloatingActionButton
                    label={'Review Model Output'}
                    onClick={uploadParameters}
                    loading={uploadMutation.isLoading}
                    disable={disable}
                />
            </Container>
        </>
    );
}
